<template>
    <div
        v-if="is404"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>
                    {{ $t('messages.api.account.noAccount', { id: accountId }) }}
                </p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="account.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors, validate }"
                        name="provider"
                        vid="provider_id"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('provider.provider') }} <b class="text-danger">*</b></label>

                            <FormSelect
                                v-model="account.provider"
                                :options="providers"
                                :placeholder="`${$t('select')} ${$t('provider.provider')}`"
                                :disabled="isLoading"
                                searchable
                                allowEmpty
                                closeOnSelect
                                trackBy="id"
                                label="name"
                                @close="validate"
                                @input="showCredentials"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="sending rate"
                        vid="sending_rate"
                        rules="required|numeric|min_value:0|max_value:500"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('sendingRate') }} <b class="text-danger">*</b></label>

                            <input
                                v-model.number="account.sendingRate"
                                type="number"
                                class="form-control"
                                :min="sendingRateMin"
                                :max="sendingRateMax"
                                step="1"
                                :placeholder="`${$t('enter')} ${$t('sendingRate')}`"
                                :disabled="isLoading"
                                @keypress="onKeypressSendingRate"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="callback domain"
                        vid="callback_domain"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('callbackDomain') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="account.callbackDomain"
                                type="text"
                                class="form-control"
                                :disabled="isLoading"
                                :placeholder="`${$t('enter')} ${$t('callbackDomain')}`"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>

                <div
                    v-if="showSenderIp"
                    class="col-xs-12 col-sm-6 col-lg-4"
                >
                    <ValidationProvider
                        v-slot="{ errors, validate }"
                        name="sender_ip"
                        slim
                        vid="sender_ip"
                    >
                        <div
                            :class="{ 'has-error': errors.length }"
                            class="form-group"
                        >
                            <label>{{ $t('senderIp.senderIps') }}</label>

                            <FormSelect
                                v-model="account.senderIp"
                                :disabled="isLoading"
                                :options="senderIps"
                                :placeholder="`${$t('select')} ${$t('senderIp.senderIp')}`"
                                allowEmpty
                                closeOnSelect
                                label="name"
                                searchable
                                trackBy="id"
                                @close="validate"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />

                            <p class="help-block">
                                {{ account.provider.useSenderIp ? $t('senderIp.hintSending') : $t('senderIp.hintBillingOnly') }}
                            </p>

                            <p class="help-block">
                                <span v-if="senderIpSendingProviders">
                                    {{ $t('senderIp.sendingProviders', { providers: senderIpSendingProviders }) }}
                                </span>
                                <br v-if="senderIpSendingProviders && senderIpBillingOnlyProviders" />
                                <span v-if="senderIpBillingOnlyProviders">
                                    {{ $t('senderIp.billingOnlyProviders', { providers: senderIpBillingOnlyProviders }) }}
                                </span>
                            </p>
                        </div>
                    </ValidationProvider>
                </div>

                <div
                    v-if="account.provider.credentials"
                    class="col-xs-12"
                >
                    <strong>API Credentials</strong>
                    <div class="table-responsive no-padding">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th class="grid-width-30">Key</th>
                                    <th class="grid-width-50">Value</th>
                                    <th class="text-right">
                                    </th>
                                </tr>
                            </thead>
                            <tbody id="api-credentials">
                                <tr
                                    v-for="(credential) in account.provider.credentials"
                                    class="api-creds-item"
                                >
                                    <td>
                                        <div class="form-group no-margin">
                                            <input
                                                :value="credential"
                                                class="form-control input-sm"
                                                readonly
                                                type="text"
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <ValidationProvider
                                            v-slot="{ errors }"
                                            :name="credential"
                                            :vid="credential"
                                            rules="required"
                                            slim
                                        >
                                        <div
                                            :class="{ 'has-error': errors.length }"
                                            class="form-group no-margin"
                                        >
                                            <input
                                                v-model="account.credentials[credential]"
                                                class="form-control input-sm"
                                                type="text"
                                            />
                                            <div
                                                v-if="errors.length"
                                                class="help-block"
                                                v-text="errors[0]"
                                            />
                                        </div>
                                        </ValidationProvider>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <template v-if="isEdit">
                <hr />

                <div class="row">
                    <div class="col-xs-12">
                        <strong> {{ $t('api.apis') }}: </strong>

                        <p>
                            <ValueListEmpty
                                :list="account.apis"
                                href="/apis/"
                            />
                        </p>
                    </div>
                </div>
            </template>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-8">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isLoading || invalid"
                        @click="submitAccount"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isLoading"
                        @click="
                            $router.push({
                                name: 'apis.accounts.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>

                <div
                    v-if="isEdit"
                    class="col-xs-4 text-right"
                >
                    <button
                        class="btn btn-sm btn-danger btn-xs-block"
                        type="button"
                        data-toggle="modal"
                        data-target="#delete-modal"
                        :disabled="isLoading"
                    >
                        {{ $t('delete') }}
                    </button>
                </div>
            </div>
        </div>

        <ApisAccountsModalDelete
            :account="account"
            @deleted="onDeleted"
        />
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import ValueListEmpty from '@/components/subcomponents/ValueListEmpty';
import ApisAccountsModalDelete from '@/components/apis/accounts/ApisAccountsModalDelete';

export default {
    components: {
        ValueListEmpty,
        ApisAccountsModalDelete,
    },

    props: {
        accountId: {
            type: Number,
            default: undefined,
        },
    },

    data() {
        return {
            is404: false,
            isLoading: false,

            sendingRateMin: 0,
            sendingRateMax: 500,
            providers: [],
            senderIps: [],

            account: {
                name: '',
                provider: {},
                senderIp: {},
                callbackDomain: '',
                sendingRate: null,
                apis: [],
                credentials: {},
            },
        };
    },

    computed: {
        isEdit() {
            return !!this.accountId;
        },
        showSenderIp() {
            return (
                this.account.provider.useSenderIp ||
                this.providers.some((provider) => provider.id === this.account.provider.id && provider.supportsBilling)
            );
        },
        senderIpSendingProviders() {
            return this.providers
                .filter((provider) => provider.useSenderIp)
                .map((provider) => provider.name)
                .join(', ');
        },
        senderIpBillingOnlyProviders() {
            return this.providers
                .filter((provider) => provider.supportsBilling && !provider.useSenderIp)
                .map((provider) => provider.name)
                .join(', ');
        },
    },

    async created() {
        this.isLoading = true;

        if (this.isEdit) {
            await this.fetchApiAccount();
        } else {
            await this.fetchCommon();
        }

        this.isLoading = false;
        this.$emit('loaded', this.account);
    },

    methods: {
        async fetchApiAccount() {
            try {
                this.isLoading = true;

                const {
                    data: { account },
                } = await axios.get('/apis/accounts/data/account', {
                    params: {
                        id: this.accountId,
                    },
                });

                this.account = account;

                await this.fetchCommon();
            } catch (error) {
                this.is404 = error.response.status === 404;
            }
        },

        showCredentials() {
            this.account.credentials = {};
            this.$nextTick(() => {
                this.$refs.observer.reset();
            });
        },

        async fetchCommon() {
            const {
                data: { providers, senderIps },
            } = await axios.get(`/apis/accounts/data/common`);

            this.providers = providers || [];
            this.senderIps = senderIps || [];
        },

        async submitAccount() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const { provider, senderIp, ...accountData } = this.account;

                const account = {
                    ...accountData,
                    providerId: provider.id,
                    senderIp: senderIp ? senderIp.id : null,
                };

                const {
                    data: { action },
                } = await axios({
                    method: this.isEdit ? 'PUT' : 'POST',
                    url: '/apis/accounts/data/account',
                    data: account,
                });

                if (action === 'created') {
                    this.onCreated(account.name);
                } else if (action === 'updated') {
                    this.onUpdated(account.id, account.name);
                }
            } catch (error) {
                const responseData = (error.response && error.response.data) || {};
                const errors = responseData.errors;
                const message = responseData.message;

                if (errors) {
                    this.$refs.observer.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: message || error.message || 'Unknown error',
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },

        async onDeleted() {
            return await this.$router.replace({
                name: 'apis.accounts.list',
            });
        },

        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'apis.accounts.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.api.account.updated', { name }),
            });
        },

        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'apis.accounts.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.api.account.created', { name }),
            });
        },

        onKeypressSendingRate(e) {
            if (!String.fromCharCode(e.which).match(/[0-9]/)) {
                e.preventDefault();
                return false;
            }
        },
    },
};
</script>
